import React from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  Alert,
  AlertTitle,
} from "@mui/material";
import { Refresh, AccessTime } from "@mui/icons-material";
import { motion } from "framer-motion";

const ErrorState = ({ error, onRetry, themeMode = "dark" }) => {
  const handleRefresh = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const getErrorMessage = (error) => {
    if (typeof error === "string") {
      return error;
    }

    if (error?.message) {
      return error.message;
    }

    return "An unexpected error occurred";
  };

  const getErrorSuggestion = (error) => {
    const errorMessage = getErrorMessage(error).toLowerCase();

    if (
      errorMessage.includes("not found") ||
      errorMessage.includes("job not found")
    ) {
      return "The requested project could not be found. Please check the URL.";
    }

    if (errorMessage.includes("network") || errorMessage.includes("fetch")) {
      return "There seems to be a network issue. Please check your internet connection and try again.";
    }

    if (errorMessage.includes("dropbox") || errorMessage.includes("folder")) {
      return "There was an issue accessing the project files. The files may still be processing or there may be a temporary issue.";
    }

    return "Please try refreshing the page.";
  };

  const errorMessage = getErrorMessage(error);
  const isJobNotReady = /no project folders found/i.test(errorMessage);
  const isDarkMode = themeMode === "dark";

  const jobReadyColors = {
    background: isDarkMode
      ? "linear-gradient(145deg, rgba(25,118,210,0.09), rgba(144,202,249,0.05))"
      : "linear-gradient(145deg, rgba(30,136,229,0.12), rgba(187,222,251,0.35))",
    border: isDarkMode
      ? "1px solid rgba(144, 202, 249, 0.25)"
      : "1px solid rgba(30, 136, 229, 0.25)",
    shadow: isDarkMode
      ? "0 4px 12px rgba(33, 150, 243, 0.04)"
      : "0 6px 15px rgba(13, 71, 161, 0.05)",
    title: isDarkMode ? "#e3f2fd" : "#0d47a1",
    body: isDarkMode ? "rgba(227, 242, 253, 0.9)" : "rgba(13, 71, 161, 0.9)",
    muted: isDarkMode ? "rgba(227, 242, 253, 0.8)" : "rgba(13, 71, 161, 0.75)",
    iconRing: isDarkMode
      ? "rgba(144, 202, 249, 0.25)"
      : "rgba(30, 136, 229, 0.25)",
    innerGlow: isDarkMode
      ? "rgba(144, 202, 249, 0.05)"
      : "rgba(187, 222, 251, 0.25)",
    iconBorder: isDarkMode
      ? "2px solid rgba(144, 202, 249, 0.4)"
      : "2px solid rgba(30, 136, 229, 0.4)",
    iconBackground: isDarkMode
      ? "rgba(13, 71, 161, 0.08)"
      : "rgba(227, 242, 253, 0.7)",
    iconColor: isDarkMode ? "#90caf9" : "#1565c0",
  };

  const alertColors = {
    background: isDarkMode
      ? "rgba(244, 67, 54, 0.05)"
      : "rgba(244, 67, 54, 0.03)",
    text: isDarkMode ? "#ff5722" : "#c62828",
    border: isDarkMode
      ? "1px solid rgba(244, 67, 54, 0.2)"
      : "1px solid rgba(244, 67, 54, 0.15)",
    icon: isDarkMode ? "#f44336" : "#d32f2f",
    titleGradient: isDarkMode
      ? "linear-gradient(135deg, #ff5722 0%, #f44336 100%)"
      : "linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)",
    body: isDarkMode ? "rgba(255, 255, 255, 0.9)" : "rgba(33, 33, 33, 0.9)",
    secondary: isDarkMode
      ? "rgba(255, 255, 255, 0.7)"
      : "rgba(55, 71, 79, 0.85)",
  };

  const supportBoxStyles = {
    background: isDarkMode
      ? "rgba(255, 255, 255, 0.02)"
      : "rgba(0, 0, 0, 0.015)",
    border: isDarkMode
      ? "1px solid rgba(255, 255, 255, 0.1)"
      : "1px solid rgba(0, 0, 0, 0.08)",
    text: isDarkMode ? "rgba(255, 255, 255, 0.8)" : "rgba(33, 33, 33, 0.85)",
  };

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: 3,
          textAlign: "center",
        }}
      >
        {isJobNotReady ? (
          <Box
            sx={{
              width: "100%",
              p: 4,
              borderRadius: 3,
              position: "relative",
              overflow: "hidden",
              background: jobReadyColors.background,
              border: jobReadyColors.border,
              boxShadow: jobReadyColors.shadow,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                textAlign: "center",
              }}
            >
              <Box sx={{ position: "relative", width: 88, height: 88 }}>
                <Box
                  component={motion.div}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    border: `1px dashed ${jobReadyColors.iconRing}`,
                  }}
                />
                <Box
                  component={motion.div}
                  animate={{ scale: [1, 1.04, 1], opacity: [0.5, 0.8, 0.5] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  sx={{
                    position: "absolute",
                    inset: 12,
                    borderRadius: "50%",
                    background: jobReadyColors.innerGlow,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    inset: 6,
                    borderRadius: "50%",
                    border: jobReadyColors.iconBorder,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: jobReadyColors.iconBackground,
                  }}
                >
                  <AccessTime
                    sx={{ color: jobReadyColors.iconColor, fontSize: "2rem" }}
                  />
                </Box>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: jobReadyColors.title,
                }}
              >
                Your project is still getting ready
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: jobReadyColors.body, maxWidth: 540 }}
              >
                Folders will appear here as soon as the files finish uploading.
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: jobReadyColors.muted, maxWidth: 560 }}
              >
                If you&rsquo;ve received an email saying your job is ready, wait a couple of minutes and then refresh so the files have time to sync in.
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: jobReadyColors.muted, maxWidth: 560 }}
              >
                We&rsquo;ll keep preparing things in the background, so you don&rsquo;t need to do anything else right now.
              </Typography>
            </Box>
          </Box>
        ) : (
          <Alert
            severity="error"
            sx={{
              width: "100%",
              background: alertColors.background,
              color: alertColors.text,
              border: alertColors.border,
              borderRadius: 3,
              backdropFilter: "blur(10px)",
              "& .MuiAlert-message": {
                width: "100%",
              },
              "& .MuiAlert-icon": {
                color: alertColors.icon,
              },
            }}
          >
            <AlertTitle
              sx={{
                fontWeight: 700,
                fontSize: "1.2rem",
                background: alertColors.titleGradient,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Unable to Load Project
            </AlertTitle>
            <Typography
              variant="body1"
              gutterBottom
              sx={{ color: alertColors.body }}
            >
              {errorMessage}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: alertColors.secondary }}
            >
              {getErrorSuggestion(error)}
            </Typography>
          </Alert>
        )}

        <Box
          sx={{
            display: "flex",
            gap: 3,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            size="large"
            sx={{
              background: isJobNotReady
                ? "linear-gradient(135deg, #42a5f5 0%, #1565c0 100%)"
                : "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)",
              color: "white",
              fontWeight: 600,
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              boxShadow: isJobNotReady
                ? "0 4px 18px rgba(66, 165, 245, 0.4)"
                : "0 4px 12px rgba(244, 67, 54, 0.3)",
              "&:hover": {
                background: isJobNotReady
                  ? "linear-gradient(135deg, #1e88e5 0%, #0d47a1 100%)"
                  : "linear-gradient(135deg, #d32f2f 0%, #c62828 100%)",
                transform: "translateY(-1px)",
                boxShadow: isJobNotReady
                  ? "0 6px 20px rgba(33, 150, 243, 0.5)"
                  : "0 6px 16px rgba(244, 67, 54, 0.4)",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            {isJobNotReady ? "Refresh" : "Try Again"}
          </Button>
        </Box>

        <Box
          sx={{
            mt: 3,
            p: 3,
            background: supportBoxStyles.background,
            backdropFilter: "blur(10px)",
            border: supportBoxStyles.border,
            borderRadius: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: supportBoxStyles.text }}
          >
            {isJobNotReady
              ? "Still nothing after a few minutes? Email us at "
              : "Not loading? Email us at "}
            <a
              href="mailto:production@imagesthatsell.com.au"
              style={{
                color: "#2196f3",
                textDecoration: "none",
                fontWeight: 500,
              }}
            >
              production@imagesthatsell.com.au
            </a>
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default ErrorState;
